import React, { useEffect, useState } from "react";
import { Icon, IconButton } from "@components/atoms";
import { CachedImage, TextField } from "@components/molecules";

import * as S from "./styles";
import { IProps } from "./types";

const QuantityButtonAdd = (
  add: () => void,
  index?: number
) => (
  <S.QuantityButtons data-test="quantityControls">
    <div onClick={add} data-test="increaseButton">
      <Icon size={16} name="plus" />
    </div>
  </S.QuantityButtons>
);
const QuantityButtonSubtract = (
  subtract: () => void,
  index?: number
) => (
  <S.QuantityButtons data-test="quantityControls">
    <div onClick={subtract} data-test="subtractButton">
      <Icon size={16} name="horizontal_line" />
    </div>
  </S.QuantityButtons>
);
/**
 * Product row displayed on cart page
 */
export const CartRow: React.FC<IProps> = ({
  index,
  totalPrice,
  unitPrice,
  name,
  sku,
  quantity,
  maxQuantity,
  onQuantityChange,
  thumbnail,
  attributes = [],
  onRemove,
  onProductClick,
  id,
}: IProps) => {
  const [tempQuantity, setTempQuantity] = useState<string>(quantity.toString());
  const [isTooMuch, setIsTooMuch] = useState(false);

  const handleBlurQuantityInput = () => {
    let newQuantity = parseInt(tempQuantity, 10);

    if (isNaN(newQuantity) || newQuantity <= 0) {
      newQuantity = quantity;
    } else if (newQuantity > maxQuantity) {
      newQuantity = maxQuantity;
    }

    if (quantity !== newQuantity) {
      onQuantityChange(newQuantity);
    }

    const newTempQuantity = newQuantity.toString();
    if (tempQuantity !== newTempQuantity) {
      setTempQuantity(newTempQuantity);
    }

    setIsTooMuch(false);
  };

  useEffect(() => {
    setTempQuantity(quantity.toString());
  }, [quantity]);

  const add = React.useCallback(
    () => quantity < maxQuantity && onQuantityChange(quantity + 1),
    [quantity]
  );
  const subtract = React.useCallback(
    () => quantity > 1 && onQuantityChange(quantity - 1),
    [quantity]
  );
  const handleQuantityChange = (evt: React.ChangeEvent<any>) => {
    const newQuantity = parseInt(evt.target.value, 10);

    setTempQuantity(evt.target.value);

    setIsTooMuch(!isNaN(newQuantity) && newQuantity > maxQuantity);
  };

  const quantityErrors = isTooMuch
    ? [
        {
          message: `Maximum quantity is ${maxQuantity}`,
        },
      ]
    : undefined;

  const windowSize =  window.onresize = () => {
    if(window.innerWidth <= 540 )
    {return true}
    else{return false}
  }

  return (
    <S.Wrapper data-test="cartRow" data-test-id={sku}>
      <S.Photo onClick={onProductClick}>
        <CachedImage data-test="itemImage" {...thumbnail} />
      </S.Photo>
      <S.Description onClick={onProductClick}>
        <S.Name data-test="itemName">
          {name}
        </S.Name>
        <S.Attributes data-test="itemAttributes">
          {attributes.map(({ attribute, values }, attributeIndex) => (
            <S.SingleAttribute key={attribute.id}>
              <span
                data-test="itemSingleAttribute"
                data-test-id={attributeIndex}
              >
                <S.LightFont>{attribute.name}:</S.LightFont>{" "}
                {values.map(value => value.name).join(", ")}
              </span>
            </S.SingleAttribute>
          ))}
        </S.Attributes>
        {/* <S.Sku>
          <S.LightFont>
            SKU: <span data-test="itemSKU">{sku ? sku : "-"}</span>
          </S.LightFont>
        </S.Sku> */}
      </S.Description>
      <S.Quantity>
        <TextField
          name="quantity"
          label="Quantity"
          value={tempQuantity}
          onBlur={handleBlurQuantityInput}
          onChange={handleQuantityChange}
          contentLeft={(QuantityButtonSubtract(subtract,index))}
          contentRight={QuantityButtonAdd(add, index)}
          errors={quantityErrors}
          borderRadius={windowSize() ? 30 : 5 }
        />
      </S.Quantity>
      <S.Trash>
        <IconButton
          testingContext="removeButton"
          testingContextId={sku}
          size={22}
          name="trash"
          onClick={onRemove}
        />
      </S.Trash>

      <S.TotalPrice>
        <S.PriceLabel>
          <S.LightFont>Total Price:</S.LightFont>
        </S.PriceLabel>
        <p data-test="totalPrice">{totalPrice}</p>
      </S.TotalPrice>
      <S.UnitPrice>
        <S.PriceLabel>
          <S.LightFont>Price:</S.LightFont>
        </S.PriceLabel>
        <p data-test="unitPrice">{unitPrice}</p>
      </S.UnitPrice>
    </S.Wrapper>
  );
};
